import React from 'react';
import { Moon, Sun, Palette } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useLanguage } from '../../contexts/LanguageContext';

interface ThemeToggleProps {
  showLabel?: boolean;
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  showLabel = false, 
  className = '' 
}) => {
  const { theme, toggleTheme } = useTheme();

  const handleToggle = () => {
    toggleTheme();
    
    // Add haptic feedback for Telegram WebApp
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
  };

  return (
    <button
      onClick={handleToggle}
      className={`flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors ${className}`}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      <div className="w-5 h-5 mr-3 flex items-center justify-center">
        {theme === 'light' ? (
          <Sun className="w-5 h-5 text-yellow-500 fill-current" />
        ) : (
          <Moon className="w-5 h-5 text-primary fill-current" />
        )}
      </div>
      {showLabel && (
        <span className="font-medium">
          {theme === 'light' ? 'Light Mode' : 'Dark Mode'}
        </span>
      )}
    </button>
  );
};

// Alternative compact version for sidebar
export const SidebarThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  const handleToggle = () => {
    toggleTheme();
    
    // Add haptic feedback for Telegram WebApp
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
  };

  return (
    <button
      onClick={handleToggle}
      className="w-full flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      <Palette className="w-5 h-5 mr-3" />
      <span className="font-medium">{t('sidebar.theme')}</span>
      <div className="ml-auto">
        {theme === 'light' ? (
          <Sun className="w-4 h-4 text-yellow-500 fill-current" />
        ) : (
          <Moon className="w-4 h-4 text-primary fill-current" />
        )}
      </div>
    </button>
  );
};